import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Input, Popup, SemanticICONS, Tab } from 'semantic-ui-react';
import i18n from '~/components/i18n';
import Layout from '~/components/Layout'
import { useDebounce } from 'use-lodash-debounce';
import TableComponent from '~/components/TableComponent';
import { RouteTypes } from '~/routes/constants';
import { CoolFarmQuery } from '~/QueryHooks/CoolFarmQuery';
import { Link } from 'react-router-dom'
import CropsDialog from './CropsDialog';
import styled from 'styled-components';
import CowActionSvg from '@assets/svgs/cool-farm-cow.svg'
import CropActionImg from '@assets/svgs/cool-farm-crops.png'
import CustomIcon from '~/components/CustomIcon';
import PreviewModal from './PreviewModal';

const getDefaultTableColumnHeaders = () => [
  {
    name: i18n.t('ID'),
    key: 'uniqueId'
  },
  {
    name: i18n.t('Period under consideration'),
    key: 'period',
    type: 'climate-check-period'
  },
  {
    name: i18n.t('Title/Topic'),
    key: 'title'
  },
  {
    name: i18n.t('Start/end data collection'),
    key: 'start',
    type: 'startEndSurvey',
    sorted: 'desc'
  },
  {
    name: i18n.t('Actions')
  }
];

const StyledDiv = styled.div`
margin-right: 7px;
`

const CoolFarm: React.FC = () => {
  const { t } = useTranslation()

  const [search, setSearch] = useState('')
  const [dataFiltered, setDataFiltered] = useState({
    page: 0,
    rows: 20,
    search: '',
    status: 'actual',
    column: '',
    sort: '',
    farmerId: ''
  })
  const [tableColumnHeaders, setTableColumnHeaders] = useState([])
  const [activeTabIndex, setActiveTabIndex] = useState(0)
  const [uniqueId, setUniqueId] = useState(null)
  const [openCropModalId, setOpenCropModalId] = useState(null)
  const [previewModalId, setPreviewModalId] = useState(null)

  const debouncedSearchValue = useDebounce(search, 300)

  const actions = [
    {
      popupMessage: i18n.t('Answer cow'),
      type: 'link',
      name: 'truck',
      size: 'large',
      renderActions: getActions,
      actionType: 'answer-cow',
      link: RouteTypes.COOL_FARM_ANSWER_DAIRY
    },
    {
      popupMessage: i18n.t('Answer crop'),
      type: 'link',
      name: 'crop',
      size: 'large',
      link: RouteTypes.COOL_FARM_ANSWER_CROP,
      renderActions: getActions,
      actionType: 'answer-crop'
    },
    {
      popupMessage: i18n.t('Show'),
      name: 'eye',
      type: 'icon',
      renderActions: getActions,
      actionType: 'show-co2'
    }
  ]

  const {
    data: coolFarmOptions,
    isLoading: isBusyOptions,
    refetch: fetchOptions
  } = CoolFarmQuery.getCoolFarmOptions({
    ...dataFiltered,
    page: dataFiltered.page === 0 ? dataFiltered.page : dataFiltered.page - 1
  })

  const {
    data: coolFarmData,
    isLoading: isBusyData,
    refetch: fetchFilteredData,
    isFetching: isFetchingData
  } = CoolFarmQuery.getCoolFarmFootprints({
    ...dataFiltered,
    page: dataFiltered.page === 0 ? dataFiltered.page : dataFiltered.page - 1
  })

  const {
    data: coolFarmCount,
    isLoading: isBusyCount,
    refetch: fetchCount
  } = CoolFarmQuery.getCoolFarmCount({
    ...dataFiltered,
    page: dataFiltered.page === 0 ? dataFiltered.page : dataFiltered.page - 1,
    search: debouncedSearchValue
  })

  useEffect(() => {
    setTableColumnHeaders(getDefaultTableColumnHeaders())
    fetchFilteredData()
    fetchOptions()
  }, [t])

  useEffect(() => {
    setTableColumnHeaders(getDefaultTableColumnHeaders())
    fetchFilteredData()
    fetchOptions()
  }, [t])
  
  useEffect(() => {
    if (dataFiltered) {
      fetchFilteredData();
    }
  }, [dataFiltered]);
  
  useEffect(() => {
    setDataFiltered({ ...dataFiltered, search: debouncedSearchValue, page: 0 });
  }, [debouncedSearchValue]);

  function renderLinkCropAction(action, id, imgSrc) {
    const link = action.link.replace(':id', id)
    return <Popup
      content={action.popupMessage}
      trigger={<Link to={link}>
        <img style={{ height: '1.5em', width: '1.5em', marginRight: 5 }} src={imgSrc}/>
      </Link>}
    />
  }

  function renderHasCropsAction(crops, id) {
    return <Popup
      content={<div>
        {crops?.map(crop => {
          return <div key={crop?.id}>{crop?.name || ''}</div>
        })}
      </div>}
      trigger={ <StyledDiv
        onClick={() => setOpenCropModalId(id)}
        style={{ cursor: 'pointer' }}
      >
        <img style={{ height: '1.5em', width: '1.5em' }} src={CropActionImg}/>
        <img style={{ height: '1.5em', width: '1.5em' }} src={CropActionImg}/>
      </StyledDiv>}
    />
  }

  function renderCowAction(action, id, imgSrc) {
    const link = action.link.replace(':id', id)
    return <Popup
      content={action.popupMessage}
      trigger={<Link to={link}>
        <img style={{ height: '1.8em', width: '1.8em', marginRight: 5 }} src={imgSrc}/>
      </Link>}
    />
  }

  function handleShow(row) {
    setPreviewModalId(row?.id)
    setUniqueId(row?.uniqueId)
  }

  function getActions(cropData, actionType) {
    const { id, completed } = cropData
    const action = actions.find(action => action.actionType === actionType)
    const hasCrops = cropData?.crops?.length > 0
    const displayShowAction = completed === 1

    switch(actionType) {
      case 'answer-crop':
        return hasCrops ? renderHasCropsAction(cropData.crops, cropData.id) : renderLinkCropAction(action, id, CropActionImg);
      case 'answer-cow':
        return renderCowAction(action, id, CowActionSvg)
      case 'show-co2':
        return displayShowAction && <Popup
          content={action.popupMessage}
          trigger={<CustomIcon
            style={{ cursor: 'pointer', marginBottom: 8, marginLeft: 5 }}
            onClick={() => handleShow(cropData)}
            name={action.name as SemanticICONS}
            size='large'
            />}
          />
      default:
        return
    }
  }

  function renderSearch() {
    return (
      <Input
        icon={{ name: 'search', circular: true, link: true }}
        placeholder={t('Search...')}
        value={search}
        onChange={(e, data) => setSearch(data.value)}
      />
    );
  }

  function renderContent() {
    return <TableComponent
      actions={actions}
      removePagination
      animated
      isFetchingData={isFetchingData}
      tableColumnHeaders={tableColumnHeaders}
      dataFiltered={dataFiltered}
      handleChangeDataFiltered={(data, tableColumnHeaders) => {
        setTableColumnHeaders(tableColumnHeaders)
        setDataFiltered(data)
      }}
      rows={rows}
      total={filtered}
    />
  }

  function handleTabChange(activeIndex) {
    if (!isFetchingData) {
      const status = coolFarmOptions?.statuses?.find((_, indexOption) => indexOption === activeIndex);
      setDataFiltered({
        ...dataFiltered,
        status: status.key,
        page: 0
      });
      setActiveTabIndex(activeIndex)
    }
  }

  function renderPanes() {
    const panes = coolFarmOptions?.statuses?.map((option) => {
      const countIndex = coolFarmCount.findIndex((count) => count.status == option.key);
      return {
        menuItem: `${option.label}  (${coolFarmCount[countIndex]?.count || 0})`,
        render: () => renderContent()
      };
    });
 
    return (
      <Tab
        menu={{ secondary: true, pointing: true }}
        activeIndex={activeTabIndex}
        panes={panes}
        onTabChange={(_, data) => handleTabChange(data.activeIndex)}
       />
    );
  }

  function renderPreviewModal() {
    return <PreviewModal
      id={previewModalId}
      handleClose={() => {
        setPreviewModalId(null)
        setUniqueId(null)
      }}
      farmerId={dataFiltered.farmerId}
      uniqueId={uniqueId}
    />
  }

  const { filtered, rows } = coolFarmData ? coolFarmData : { filtered: 0, rows: [] };
  const isLoading = isBusyData || isBusyCount || isBusyOptions

  return <Layout
    title={t('CoolFarmTool')}
    filters={renderSearch()}
    segmented
    isBusy={isLoading}
    isCo2Loader={true}
  >
    {renderPreviewModal()}
    {coolFarmCount && coolFarmOptions && coolFarmData && renderPanes()}
    <CropsDialog
      open={Boolean(openCropModalId)}
      onClose={() => setOpenCropModalId(null)}
      footprintId={openCropModalId}
    />
  </Layout>
}
export default CoolFarm